/**
 * The observation primitive the whole viewer stack shares.
 *
 * Deliberately hand-rolled rather than reusing `signalFactory` from
 * `@frontend/controllers`: this package must stay free of runtime dependencies
 * (signalFactory imports `symbol-observable`), and `subscribe` has to return a bare
 * unsubscribe function, which is the shape `useSyncExternalStore` requires.
 *
 * `ReadableStore` is the important half. It is the vocabulary a lower layer uses to
 * observe state an upper layer owns — the renderer watches a viewport store without
 * ever naming the session that owns it — so dependencies keep pointing downward.
 */

/** Read side: what a consumer needs to observe state it does not own. */
export type ReadableStore<S> = {
  getState(): S;
  /** Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void;
};

export type Store<S> = ReadableStore<S> & {
  /**
   * Applies `recipe` to the current state. A recipe that returns the state it was
   * given is a no-op — no notification — so a reducer can bail out cheaply rather
   * than the caller having to test first.
   */
  setState(recipe: (previous: S) => S): void;
};

export const createStore = <S>(initial: S): Store<S> => {
  let state = initial;
  const listeners = new Set<() => void>();

  return {
    getState: () => state,

    setState(recipe) {
      const next = recipe(state);
      if (Object.is(next, state)) {
        return;
      }
      state = next;
      // Copied before iterating: a listener may unsubscribe itself — or another —
      // while being notified, and mutating a Set mid-iteration would skip entries.
      [...listeners].forEach((listener) => listener());
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

/**
 * A read-only view of one slice of another store.
 *
 * Subscribers are notified only when the *selected* value changes, so a consumer
 * that cares about layer visibility is not woken by a change to something else in
 * the same state object. This is what lets a lower layer take a narrow
 * `ReadableStore` instead of the whole state it happens to live in.
 */
export const deriveStore = <S, T>(
  source: ReadableStore<S>,
  select: (state: S) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): ReadableStore<T> => {
  let cachedSource = source.getState();
  let cachedValue = select(cachedSource);

  const getState = (): T => {
    const state = source.getState();
    if (!Object.is(state, cachedSource)) {
      cachedSource = state;
      const next = select(state);
      if (!isEqual(cachedValue, next)) {
        cachedValue = next;
      }
    }
    return cachedValue;
  };

  return {
    getState,
    subscribe(listener) {
      let previous = getState();
      return source.subscribe(() => {
        const next = getState();
        if (Object.is(next, previous)) {
          return;
        }
        previous = next;
        listener();
      });
    },
  };
};

/**
 * A derived store over several sources.
 *
 * `deriveStore` is one source, one selector. Reach for this when the value is a
 * function of more than one store — a highlight that depends on both what is
 * selected *and* the document it is drawn from, for example. Subscribers are
 * notified when any source notifies, then silenced by `isEqual` when the
 * derived value did not change.
 *
 * `select` reads the sources itself (typically via `getState`) rather than
 * receiving their snapshots. That keeps the helper from growing a tuple of
 * type parameters for each arity, and matches how a controller already derives
 * across stores it owns.
 */
export const combineStores = <T>(
  sources: readonly ReadableStore<unknown>[],
  select: () => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): ReadableStore<T> => {
  let cachedSources = sources.map((source) => source.getState());
  let cachedValue = select();

  const getState = (): T => {
    const nextSources = sources.map((source) => source.getState());
    const sourcesUnchanged = nextSources.every((state, index) =>
      Object.is(state, cachedSources[index]),
    );
    if (sourcesUnchanged) {
      return cachedValue;
    }

    cachedSources = nextSources;
    const next = select();
    if (!isEqual(cachedValue, next)) {
      cachedValue = next;
    }
    return cachedValue;
  };

  return {
    getState,
    subscribe(listener) {
      let previous = getState();
      const notify = () => {
        const next = getState();
        if (Object.is(next, previous)) {
          return;
        }
        previous = next;
        listener();
      };
      const unsubs = sources.map((source) => source.subscribe(notify));
      return () => {
        unsubs.forEach((unsubscribe) => unsubscribe());
      };
    },
  };
};

/**
 * A read/write view of one field of another store.
 *
 * The write-side counterpart to `deriveStore`. Where that gives a consumer a
 * `ReadableStore` for a slice it only ever reads, this gives a `Store` for a
 * slice it owns — get, set, and subscribe, scoped to exactly that field. The
 * round-trip through `parent.setState` stays the single place the no-op check
 * and the listener fan-out happen, so a lens is never a second source of
 * truth for the value it exposes, only a narrower door onto the same one.
 */
export const createLens = <S, T>(
  parent: Store<S>,
  get: (state: S) => T,
  set: (state: S, value: T) => S,
  isEqual: (a: T, b: T) => boolean = Object.is,
): Store<T> => {
  const derived = deriveStore(parent, get, isEqual);

  return {
    getState: derived.getState,
    subscribe: derived.subscribe,
    setState(recipe) {
      parent.setState((parentState) => {
        const current = get(parentState);
        const next = recipe(current);
        return isEqual(next, current) ? parentState : set(parentState, next);
      });
    },
  };
};
